// ----------------------------------------------------------------------------
// OwnerSettingsContext
// ----------------------------------------------------------------------------
// Persistent owner/admin configuration for the SaaS. Stored in localStorage so
// the wizard's choices survive a reload — replace the localStorage layer with
// a `PATCH /api/owner/settings` call against an authenticated admin endpoint
// when wiring up a real backend.
//
// Schema:
//   openAiKey         — string (masked in UI)
//   targetModel       — "gpt-4o" | "claude-3.5-sonnet" | "gemini-pro"
//   stripeKey         — string
//   subscriptionFee   — number (USD/month, defaults to 99)
//   supabaseUrl       — string (BaaS project URL — Supabase or Firebase)
//   supabaseKey       — string (public anon key, masked in UI)
//   rlsEnabled        — boolean (row-level security toggle, defaults true)
//   activeScrapers    — string[] subset of regional pipes
//   initializedAt     — ISO timestamp set on successful wizard completion
//
// PRODUCTION LAUNCH NOTE:
//   const supabase = createClient(ownerSettings.supabaseUrl,
//                                 ownerSettings.supabaseKey);
//   This single line automatically switches the entire application — the
//   multi-tenant registration gate AND each user's individual data pipeline —
//   to the buyer's live cloud database instantly. The wizard captures both
//   strings; this file persists them; the auth gateway + ledger components
//   simply read them off the context.
// ----------------------------------------------------------------------------

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "scpp.ownerSettings.v2";

const DEFAULT_SETTINGS = {
  openAiKey: "",
  targetModel: "gpt-4o",
  stripeKey: "",
  subscriptionFee: 99,
  // BaaS (Backend-as-a-Service) bindings used by Step 3 of the wizard.
  supabaseUrl: "",
  supabaseKey: "",
  rlsEnabled: true,
  activeScrapers: ["Brevard/Central FL"],
  initializedAt: null,
};

const OwnerSettingsContext = createContext(null);

// Safely read settings from localStorage; falls back to defaults if absent
// or malformed.
//
// SECURITY NOTE — DEMO BUILD:
//   This file persists wizard inputs (including credential-shaped strings such
//   as openAiKey / stripeKey / supabaseKey) to localStorage so the demo can
//   survive page reloads. localStorage is JS-accessible and therefore
//   vulnerable to XSS — DO NOT ship this as-is to a production multi-tenant
//   deployment.
//
//   PRODUCTION PATH:
//     • Move every *secret* field server-side (e.g., Supabase Edge Function
//       backed by Vault / AWS Secrets Manager) and expose a server route the
//       wizard can call (POST /api/owner/settings).
//     • Keep only non-secret config (subscriptionFee, targetModel, region
//       selections, initializedAt) in localStorage.
//     • Use HTTP-only, SameSite=Lax cookies for the session token; never
//       store JWTs in localStorage.
const readFromStorage = () => {
  if (typeof window === "undefined") return DEFAULT_SETTINGS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch (err) {
    // Malformed JSON or storage unavailable — fall back to defaults and
    // surface the error so the issue isn't silenced in dev tooling.
    console.warn("[OwnerSettings] failed to parse stored settings:", err);
    return DEFAULT_SETTINGS;
  }
};

export const OwnerSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(readFromStorage);

  // Persist every change. Swap this for a debounced API PATCH later.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (err) {
      // Quota / private-mode / disabled storage — surface in dev tools so
      // missing persistence is debuggable instead of silently broken.
      console.warn("[OwnerSettings] persistence failed:", err);
    }
  }, [settings]);

  const updateSettings = useCallback((patch) => {
    setSettings((prev) => ({ ...prev, ...patch }));
  }, []);

  const commitInitialization = useCallback(() => {
    setSettings((prev) => ({ ...prev, initializedAt: new Date().toISOString() }));
  }, []);

  const value = useMemo(
    () => ({ settings, updateSettings, commitInitialization }),
    [settings, updateSettings, commitInitialization],
  );

  return (
    <OwnerSettingsContext.Provider value={value}>
      {children}
    </OwnerSettingsContext.Provider>
  );
};

export const useOwnerSettings = () => {
  const ctx = useContext(OwnerSettingsContext);
  if (!ctx)
    throw new Error("useOwnerSettings must be used inside OwnerSettingsProvider");
  return ctx;
};
